import { apiRequest } from './core/client';
import { normalizeHexColor } from '@/lib/colors';

// ============================================================================
// Auto Label Types
// ============================================================================

export interface AutoLabelTarget {
  id?: string;
  matchType: 'contact' | 'email' | 'domain';
  contactId: string | null;
  value: string | null;
  excludeEmails?: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AutoLabelDefinition {
  id: string;
  name: string;
  type: 'ai' | 'sender';
  description: string | null;
  color?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  targets: AutoLabelTarget[];
}

export interface AutoLabelTargetInput {
  matchType: 'contact' | 'email' | 'domain';
  contactId?: string | null;
  value?: string | null;
  excludeEmails?: string[];
}

export interface AppendAutoLabelTargetOptions {
  accountId?: string;
  messageId?: string;
  senderEmail?: string;
  contactState?: string | null;
}

export interface CreateAutoLabelPayload {
  name: string;
  type: AutoLabelDefinition['type'];
  description?: string;
  targets?: AutoLabelTargetInput[];
}

export interface UpdateAutoLabelPayload {
  name?: string;
  type?: AutoLabelDefinition['type'];
  description?: string | null;
}

// ============================================================================
// Mapping Helpers
// ============================================================================

function mapAutoLabelTarget(raw: unknown): AutoLabelTarget {
  const data = raw as Record<string, unknown>;
  const matchTypeRaw = typeof data.matchType === 'string' ? data.matchType : null;
  const matchType: AutoLabelTarget['matchType'] =
    matchTypeRaw === 'domain' ? 'domain' : matchTypeRaw === 'contact' ? 'contact' : 'email';

  const excludeEmailsSet = new Set<string>();
  const excludeFields = [data.excludeEmails, data.excludedEmails, data.exclude_emails];
  excludeFields.forEach((field) => {
    if (Array.isArray(field)) {
      field.forEach((value) => {
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          if (normalized && normalized.includes('@')) {
            excludeEmailsSet.add(normalized);
          }
        }
      });
    }
  });

  return {
    id: typeof data.id === 'string' ? data.id : undefined,
    matchType,
    contactId: typeof data.contactId === 'string' ? data.contactId : null,
    value: typeof data.value === 'string' ? data.value : null,
    excludeEmails: excludeEmailsSet.size ? Array.from(excludeEmailsSet) : undefined,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : null,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
  };
}

function mapAutoLabelDefinition(raw: unknown): AutoLabelDefinition {
  const data = raw as Record<string, unknown>;
  const targetsRaw = Array.isArray(data.targets) ? data.targets : [];
  const metadata = data.metadata && typeof data.metadata === 'object'
    ? (data.metadata as Record<string, unknown>)
    : null;
  const colorValue =
    typeof data.color === 'string'
      ? data.color
      : typeof data.colorHex === 'string'
        ? data.colorHex
        : metadata && typeof metadata.color === 'string'
          ? metadata.color
          : null;
  const color = normalizeHexColor(colorValue);

  return {
    id: typeof data.id === 'string' ? data.id : crypto.randomUUID(),
    name: typeof data.name === 'string' ? data.name : 'Label',
    type: data.type === 'ai' ? 'ai' : 'sender',
    description: typeof data.description === 'string' ? data.description : null,
    color,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : null,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
    targets: targetsRaw.map((target) => mapAutoLabelTarget(target)),
  };
}

// ============================================================================
// Auto Label Operations
// ============================================================================

export async function fetchAutoLabels(
  options: { includeTargets?: boolean } = {}
): Promise<AutoLabelDefinition[]> {
  const response = await apiRequest<unknown>({
    path: '/auto-labels',
    query: options.includeTargets ? { includeTargets: 'true' } : undefined,
  });
  const record = response as Record<string, unknown>;
  const labelsArray = Array.isArray(record.labels)
    ? record.labels
    : Array.isArray(response)
      ? (response as unknown[])
      : [];
  return labelsArray.map((item) => mapAutoLabelDefinition(item));
}

export async function createAutoLabel(
  payload: CreateAutoLabelPayload
): Promise<AutoLabelDefinition> {
  const response = await apiRequest<unknown>({
    path: '/auto-labels',
    method: 'POST',
    body: payload,
  });
  const record = response as Record<string, unknown>;
  return mapAutoLabelDefinition(record.label ?? response);
}

export async function updateAutoLabel(
  labelId: string,
  updates: UpdateAutoLabelPayload
): Promise<AutoLabelDefinition> {
  const response = await apiRequest<unknown>({
    path: `/auto-labels/${encodeURIComponent(labelId)}`,
    method: 'PATCH',
    body: updates,
  });
  const record = response as Record<string, unknown>;
  return mapAutoLabelDefinition(record.label ?? response);
}

export async function deleteAutoLabel(labelId: string): Promise<AutoLabelDefinition | null> {
  const response = await apiRequest<unknown>({
    path: `/auto-labels/${encodeURIComponent(labelId)}`,
    method: 'DELETE',
  });
  const record = response as Record<string, unknown>;
  const labelData = record.label ?? null;
  return labelData ? mapAutoLabelDefinition(labelData) : null;
}

export async function replaceAutoLabelTargets(
  labelId: string,
  targets: AutoLabelTargetInput[]
): Promise<AutoLabelTarget[]> {
  const response = await apiRequest<unknown>({
    path: `/auto-labels/${encodeURIComponent(labelId)}/targets`,
    method: 'PUT',
    body: { targets },
  });
  const record = response as Record<string, unknown>;
  const targetsArray = Array.isArray(record.targets) ? record.targets : [];
  return targetsArray.map((target) => mapAutoLabelTarget(target));
}

export async function appendAutoLabelTarget(
  labelId: string,
  target: AutoLabelTargetInput,
  options?: AppendAutoLabelTargetOptions
): Promise<AutoLabelTarget[]> {
  const response = await apiRequest<unknown>({
    path: `/auto-labels/${encodeURIComponent(labelId)}/targets`,
    method: 'POST',
    body: {
      ...target,
      ...options,
    },
  });
  const record = response as Record<string, unknown>;
  const targetsArray = Array.isArray(record.targets) ? record.targets : [];
  return targetsArray.map((entry) => mapAutoLabelTarget(entry));
}

export async function removeMessageAutoLabel(
  accountId: string,
  messageId: string,
  labelId: string
): Promise<{ removed: boolean }> {
  const response = await apiRequest<unknown>({
    path: `/messages/${encodeURIComponent(messageId)}/labels/${encodeURIComponent(labelId)}`,
    method: 'DELETE',
    query: { accountId },
  });
  const record = response as Record<string, unknown>;
  return { removed: Boolean(record.removed) };
}
